import {
  buildUseQuery,
  formatErrorMessage,
  message,
  Skeleton,
  useRouter,
} from '@main/core-ui';
import { endpoints } from '@main/pc-types';
import { SectionWrapper } from '@main/privacy-center/src/components';
import PageWrapper from '@main/privacy-center/src/components/PageWrapper';
import {
  selectRequiredPrivacyCenter,
  selectSessionProfile,
} from '@main/privacy-center/src/containers/App/selectors';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { selectDataSubjects } from '../../containers/ActiveDataSubject/selectors';
import { useStaticPrivacyCenterModule } from '../../hooks';
import { GlobalSettings } from '../ManageYourPrivacy/components';
import { selectPurposeConfigurations } from '../ManageYourPrivacy/selectors';
import { setPrivacyCenterPurposeConfiguration } from '../ManageYourPrivacy/slice';
import { communicationsPreferencesMessages } from './messages';
import { PreviewPreferences } from './PreviewPreferences';
import { UpdatePreferencesForm } from './UpdatePreferencesForm';
import { CommunicationsPreferencesWrapper } from './wrappers';

const usePurposes = buildUseQuery(endpoints.privacyCenterPurposes);
/**
 * Page for displaying user's Communications Preferences.
 */
export const CommunicationsPreferences: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { locale, formatMessage } = useIntl();
  const { location } = useRouter();
  const { showMarketingPreferences, locales } = useSelector(
    selectRequiredPrivacyCenter,
  );
  const sessionProfile = useSelector(selectSessionProfile);
  const dispatch = useDispatch();
  const purposeConfiguration = useSelector(selectPurposeConfigurations);
  const { directData, directError, skipGql } =
    useStaticPrivacyCenterModule<any>('privacyCenterPurposes');
  const subjects = useSelector(selectDataSubjects);

  const { data: gqlData, error: gqlError } = usePurposes({
    skip: skipGql,
  });

  const purposes = skipGql ? directData : gqlData;

  const getPurposesError = skipGql ? directError : gqlError;

  useEffect(() => {
    if (getPurposesError) {
      message.error(formatErrorMessage(getPurposesError.message));
    }
  }, [getPurposesError]);

  useEffect(() => {
    if (purposes) {
      dispatch(setPrivacyCenterPurposeConfiguration(purposes));
    }
  }, [purposes, dispatch]);

  if (!showMarketingPreferences) {
    return (
      <Navigate
        to={{
          ...location,
          pathname: locales.length > 1 ? `/${locale}/` : '/',
        }}
        replace
      />
    );
  }

  const renderContent = (): React.ReactNode => {
    if (!purposeConfiguration || getPurposesError) {
      return <Skeleton />;
    }
    if (!sessionProfile) {
      return <PreviewPreferences purposes={purposeConfiguration} />;
    }
    return <UpdatePreferencesForm purposes={purposeConfiguration} />;
  };

  return (
    <PageWrapper>
      <CommunicationsPreferencesWrapper>
        <GlobalSettings
          subject={subjects[0]}
          description={communicationsPreferencesMessages.selectPartitions}
          hideSelectDataSubjectType
        />
        <SectionWrapper
          title={formatMessage(communicationsPreferencesMessages.intro)}
          description={formatMessage(
            sessionProfile
              ? communicationsPreferencesMessages.loggedInMessage
              : communicationsPreferencesMessages.loggedOutMessage,
          )}
        >
          {renderContent()}
        </SectionWrapper>
      </CommunicationsPreferencesWrapper>
    </PageWrapper>
  );
};

export default CommunicationsPreferences;
